import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18n';
import Platform from 'platform';
import keys from 'config/keys';
import PLAYER_TYPES from 'constants/player-types';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import withModals from 'components/HOCs/withModals';
import ControlsSet from './ControlsSet';
import FavoriteActionsSection from './FavoriteActionsSection';
import Episodes from './Episodes';
import { StyledControlsWrapper, StyledBackButton, StyledControls, StyledPauseLabel } from '../StyledComponents';
import { ReactComponent as PauseStateIcon } from 'assets/img/player-icon-pause-state.svg';

import ProgressBar from './ProgressBar';
import Header from './Header';
import SpatialNavigation from 'libs/spatial-navigation';

const IDLE_TIMEOUT = 5000;
const PROGRESS_BAR_SECTION_NAME = 'PROGRESS_BAR';

const WITH_EPG = [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.VOD_CHANNEL, PLAYER_TYPES.CATCHUP, PLAYER_TYPES.TIMESHIFT];

const PLATFORM_KEYS = {
  PLAY: Platform.getKeyCode(keys.PLAY),
  PAUSE: Platform.getKeyCode(keys.PAUSE),
  PLAY_PAUSE: Platform.getKeyCode(keys.PLAY_PAUSE),
  RW: Platform.getKeyCode(keys.RW),
  FF: Platform.getKeyCode(keys.FF),
  STOP: Platform.getKeyCode(keys.STOP),
  BACK: Platform.getKeyCode(keys.BACK),
  RED: Platform.getKeyCode(keys.RED),
  YELLOW: Platform.getKeyCode(keys.YELLOW),
  BLUE: Platform.getKeyCode(keys.BLUE),
  GREEN: Platform.getKeyCode(keys.GREEN),
  CH_UP: Platform.getKeyCode(keys.CH_UP),
  CH_DOWN: Platform.getKeyCode(keys.CH_DOWN),
  UP: Platform.getKeyCode(keys.UP),
  DOWN: Platform.getKeyCode(keys.DOWN),
  LEFT: Platform.getKeyCode(keys.LEFT),
  RIGHT: Platform.getKeyCode(keys.RIGHT),
};

class PlayerControlsWrapper extends Component {
  idleTimeout = null;
  state = {
    idle: false,
    focusedSection: null,
    isMousePressed: false,
    isFavoriteActionsSectionOpen: false,
  };

  componentDidMount() {
    const { playerType, setMenuIdle } = this.props;
    document.body.addEventListener('keydown', this.onKeydown, true);
    document.body.addEventListener('mousedown', this.onMouseDown, true);
    document.body.addEventListener('mouseup', this.onMouseUp, true);
    document.body.addEventListener('mousemove', this.onMousemove, true);

    this.startIdleTimeout();
    if (WITH_EPG.includes(playerType)) setMenuIdle(false);
    SpatialNavigationWrapper.focus(); // STVWL-136 - Fix default focus on video player
  }

  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.onKeydown, true);
    document.body.removeEventListener('mousedown', this.onMouseDown, true);
    document.body.removeEventListener('mouseup', this.onMouseUp, true);
    document.body.removeEventListener('mousemove', this.onMousemove, true);
    this.stopIdleTimeout();
    // В некоторых случаях навигация может остаться выключенной после выхода из плеера.
    // Принудительное ее включение при componentWillUnmount фиксит это
    // https://hootko.atlassian.net/browse/STVWL-189
    // Todo: переделать это и не отключать навигацию вовсе,
    // работать с idle на уровне коллбэк функций
    SpatialNavigationWrapper.resumeNavigation();
  }

  componentDidUpdate(prevProps, prevState) {
    const { setMenuIdle, navigation, playerType } = this.props;
    const { isFavoriteActionsSectionOpen, idle } = this.state;
    if (prevState.isFavoriteActionsSectionOpen && !isFavoriteActionsSectionOpen) {
      SpatialNavigationWrapper.focus();
    }
    // Hide controls if EPG was closed
    if (this.props.isVisible && !prevProps.isVisible) {
      this.setState({ idle: true });
      if (WITH_EPG.includes(playerType)) setMenuIdle(true);
      SpatialNavigationWrapper.pauseNavigation();
    }

    if (navigation && !navigation?.prevProgram && !!prevProps.navigation?.prevProgram && !idle) {
      // STVWL-593
      SpatialNavigationWrapper.focus();
    }
  }

  getKeepControlsHidden = (keyCode) => {
    const { playerType } = this.props;
    const { idle } = this.state;

    return (
      idle &&
      [PLATFORM_KEYS.UP, PLATFORM_KEYS.DOWN].includes(keyCode) &&
      [PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT].includes(playerType)
    );
  };

  onKeydown = ({ keyCode }) => {
    const {
      onClickPause,
      onClickPlay,
      onClickFF,
      onClickRewind,
      onClickChannelUp,
      onClickChannelDown,
      onBack,
      isPlaying,
      isLive,
      playerType,
      content,
    } = this.props;
    const { isFavoriteActionsSectionOpen, idle } = this.state;
    const nonNavigationKeys = [PLATFORM_KEYS.RED, PLATFORM_KEYS.YELLOW, PLATFORM_KEYS.BLUE, PLATFORM_KEYS.GREEN];

    if (nonNavigationKeys.includes(keyCode)) return;
    const currentSectionId = SpatialNavigation.getSectionId(document.activeElement);
    if (currentSectionId?.indexOf('OVERLAY') !== -1 || currentSectionId?.indexOf('MODAL') !== -1) {
      //TEMP hack for now rewoking CONTROLS when Settings or pop-up are open;
      return;
    }

    const isLiveCatchUpOnVodChannel =
      [PLAYER_TYPES.VOD_CHANNEL].includes(playerType) && content?.state === 'LIVE_CATCHUP';

    const keepControlsHidden = this.getKeepControlsHidden(keyCode);

    const disableChannelChange = playerType === PLAYER_TYPES.TIMESHIFT && isPlaying && !isLive;

    if (!isFavoriteActionsSectionOpen && !keepControlsHidden && !idle) {
      this.startIdleTimeout();
    }

    switch (keyCode) {
      case PLATFORM_KEYS.PLAY:
        onClickPlay();
        break;
      case PLATFORM_KEYS.PAUSE:
        onClickPause();
        break;
      case PLATFORM_KEYS.PLAY_PAUSE:
        if (isPlaying) {
          onClickPause();
        } else {
          onClickPlay();
        }
        break;
      case PLATFORM_KEYS.RW:
        onClickRewind();
        break;
      case PLATFORM_KEYS.FF:
        onClickFF();
        break;
      case PLATFORM_KEYS.STOP:
        onBack();
        break;
      case PLATFORM_KEYS.CH_UP:
        if (!disableChannelChange) {
          onClickChannelDown();
        } else {
          // Just focus on controls
          SpatialNavigation.focus();
        }
        break;
      case PLATFORM_KEYS.CH_DOWN:
        if (!disableChannelChange) {
          onClickChannelUp();
        } else {
          // Just focus on controls
          SpatialNavigation.focus();
        }
        break;
      case PLATFORM_KEYS.UP:
        if (idle) {
          // Just focus on controls
          SpatialNavigation.focus();
          break;
        }

        if (!idle && !isFavoriteActionsSectionOpen) {
          if (playerType === PLAYER_TYPES.CHANNEL && currentSectionId === 'PLAYER_CONTROLS') {
            onClickChannelUp();
          }

          if (
            ([PLAYER_TYPES.TIMESHIFT].includes(playerType) || isLiveCatchUpOnVodChannel) &&
            currentSectionId === 'PROGRESS_BAR' &&
            !disableChannelChange
          ) {
            onClickChannelUp();
          }
        }
        break;
      case PLATFORM_KEYS.DOWN:
        if (idle) {
          // Just focus on controls
          SpatialNavigation.focus();
          break;
        }

        if (!idle && !isFavoriteActionsSectionOpen) {
          if (playerType === PLAYER_TYPES.CHANNEL && currentSectionId === 'PLAYER_CONTROLS') {
            onClickChannelDown();
          }

          if (
            ([PLAYER_TYPES.TIMESHIFT].includes(playerType) || isLiveCatchUpOnVodChannel) &&
            currentSectionId === 'PLAYER_CONTROLS' &&
            !disableChannelChange
          ) {
            onClickChannelDown();
          }
        }
        break;
      case PLATFORM_KEYS.LEFT:
        if (currentSectionId !== PROGRESS_BAR_SECTION_NAME) return;
        onClickRewind();
        break;
      case PLATFORM_KEYS.RIGHT:
        if (currentSectionId !== PROGRESS_BAR_SECTION_NAME) return;
        onClickFF();
        break;
      default:
      // nothing
    }
  };

  onMouseDown = () => {
    this.setState({ isMousePressed: true }, this.stopIdleTimeout);
  };

  onMouseUp = () => {
    const { isSettingsOverlayOpen } = this.props;
    const { focusedSection, isFavoriteActionsSectionOpen } = this.state;
    const isIdleShouldBeEnabled =
      focusedSection !== 'EPISODES' && !isFavoriteActionsSectionOpen && !isSettingsOverlayOpen;

    this.setState({ isMousePressed: false }, () => {
      if (isIdleShouldBeEnabled) this.startIdleTimeout();
    });
  };

  onMousemove = (e) => {
    const { isSettingsOverlayOpen } = this.props;
    const { focusedSection, isFavoriteActionsSectionOpen, isMousePressed } = this.state;
    const isIdleShouldBeEnabled =
      focusedSection !== 'EPISODES' && !isFavoriteActionsSectionOpen && !isSettingsOverlayOpen;

    e.preventDefault();
    if (isIdleShouldBeEnabled && !isMousePressed) {
      this.startIdleTimeout();
    }
  };

  startIdleTimeout = () => {
    const { playerType, setMenuIdle } = this.props;
    const { idle } = this.state;

    clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(() => {
      const { isSettingsOverlayOpen, isVisible } = this.props;
      this.setState({ idle: true });
      if (WITH_EPG.includes(playerType)) setMenuIdle(true);
      if (!isSettingsOverlayOpen && isVisible) SpatialNavigationWrapper.pauseNavigation();
    }, IDLE_TIMEOUT);

    if (idle) {
      if (WITH_EPG.includes(playerType)) setMenuIdle(false);
      this.setState({ idle: false });
      setTimeout(() => {
        SpatialNavigationWrapper.resumeNavigation();
      }, 0);
    }
  };

  stopIdleTimeout = () => {
    clearTimeout(this.idleTimeout);
  };

  onControlsFocus = () => {
    this.setState({ focusedSection: 'PLAYER_CONTROLS' });
    this.startIdleTimeout();
  };

  onEpisodeFocus = () => {
    this.setState({ focusedSection: 'EPISODES' });
    this.stopIdleTimeout();
  };

  onEpisodeEnter = (item) => {
    const { onEpisodeEnter } = this.props;

    SpatialNavigationWrapper.focus('@PLAYER_CONTROLS');

    onEpisodeEnter(item);
  };

  onClickSettings = () => {
    const { onClickSettings } = this.props;

    this.setState({ idle: true });

    onClickSettings();
  };

  onFavoriteClick = () => {
    const { playerType, content, toggleInFavoriteStatus, isLoggedIn, showLoginModal } = this.props;

    if (!isLoggedIn) {
      showLoginModal({ sectionToFocus: '@PLAYER_CONTROLS' });
      return;
    }
    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) {
      this.setState({ isFavoriteActionsSectionOpen: true });
      this.stopIdleTimeout();
    } else {
      toggleInFavoriteStatus({
        type: playerType,
        id: content.id,
      });
    }
  };

  // todo: Separate component
  getMainControls() {
    const { currentTime, currentLiveTime, duration, seekPending, playerType, content, navigation } = this.props;
    const { isResetToStartAvailable, isSettingsAvailable, isPlaying, isLive } = this.props;
    const {
      onClickPrev,
      onClickRewind,
      onClickPause,
      onClickPlay,
      onClickFF,
      onClickNext,
      onClickEPG,
      onClickOnAir,
      onClickProgress,
    } = this.props;
    const { idle } = this.state;

    const isSeriesPlayer = playerType === PLAYER_TYPES.SERIES;
    return (
      <>
        <ControlsSet
          sectionName="PLAYER_CONTROLS"
          onSectionFocus={this.onControlsFocus}
          idle={idle}
          content={content}
          playerType={playerType}
          navigation={navigation}
          isResetToStartAvailable={isResetToStartAvailable}
          isSettingsAvailable={isSettingsAvailable}
          isLive={isLive}
          isPlaying={isPlaying}
          toggleInFavoriteStatus={this.onFavoriteClick}
          onClickPrev={onClickPrev}
          onClickRewind={onClickRewind}
          onClickPause={onClickPause}
          onClickPlay={onClickPlay}
          onClickFF={onClickFF}
          onClickNext={onClickNext}
          onClickSettings={this.onClickSettings}
          onClickEPG={onClickEPG}
          onClickOnAir={onClickOnAir}
        />
        {playerType !== PLAYER_TYPES.CHANNEL && (
          <ProgressBar
            playerType={playerType}
            seekPending={seekPending}
            currentTime={currentTime}
            currentLiveTime={currentLiveTime}
            duration={duration}
            onClick={onClickProgress}
          />
        )}
        {isSeriesPlayer && content && (
          <Episodes
            onEnter={this.onEpisodeEnter}
            onFocus={this.onEpisodeFocus}
            focusedEpisodeId={content.id}
            season={content.season}
          />
        )}
      </>
    );
  }

  render() {
    const { idle, focusedSection, isFavoriteActionsSectionOpen } = this.state;

    const {
      playerType,
      content,
      onBack,
      isPlaying,
      isPaused,
      timeshiftBegin,
      toggleInFavoriteStatus,
      navigation,
      isVisible,
      isLive,
    } = this.props;

    const isWithBackButton = ![
      PLAYER_TYPES.CHANNEL,
      PLAYER_TYPES.VOD_CHANNEL,
      PLAYER_TYPES.TIMESHIFT,
      PLAYER_TYPES.CATCHUP,
    ].includes(playerType);
    const isSeriesPlayer = playerType === PLAYER_TYPES.SERIES;
    const isEpisodesFocused = focusedSection === 'EPISODES';
    const disableChannelChange = playerType === PLAYER_TYPES.TIMESHIFT && isPlaying && !isLive;

    if (!isVisible) return null;

    return (
      <StyledControlsWrapper idle={idle} darkOverlay={isEpisodesFocused}>
        {isWithBackButton && <StyledBackButton idle={idle} onEnter={onBack} />}
        {content && !isEpisodesFocused && (
          <Header
            playerType={playerType}
            content={content}
            navigation={navigation}
            timeshiftBegin={timeshiftBegin}
            idle={idle}
            disableChannelChange={disableChannelChange}
          />
        )}
        <StyledControls isSeriesPlayer={isSeriesPlayer} bottomOffset={isEpisodesFocused && 600} idle={idle}>
          {isFavoriteActionsSectionOpen ? (
            <FavoriteActionsSection
              content={content}
              toggleInFavoriteStatus={toggleInFavoriteStatus}
              onClose={() => this.setState({ isFavoriteActionsSectionOpen: false })}
              defaultSection
            />
          ) : (
            this.getMainControls()
          )}
        </StyledControls>
        {!isPlaying && isPaused && (
          <StyledPauseLabel idle={idle}>
            <PauseStateIcon />
            {i18n.t('player.buttons.pauseLabel')}
          </StyledPauseLabel>
        )}
      </StyledControlsWrapper>
    );
  }
}

PlayerControlsWrapper.propTypes = {
  playerType: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  toggleInFavoriteStatus: PropTypes.func.isRequired,
  onClickPrev: PropTypes.func.isRequired,
  onClickRewind: PropTypes.func.isRequired,
  onClickPause: PropTypes.func.isRequired,
  onClickPlay: PropTypes.func.isRequired,
  onClickFF: PropTypes.func.isRequired,
  onClickSettings: PropTypes.func.isRequired,
  onClickNext: PropTypes.func.isRequired,
  onClickChannelUp: PropTypes.func.isRequired,
  onClickChannelDown: PropTypes.func.isRequired,
  content: PropTypes.object,
  navigation: PropTypes.object,
  isResetToStartAvailable: PropTypes.bool,
  isSettingsAvailable: PropTypes.bool,
  isSettingsOverlayOpen: PropTypes.bool,
  isPlaying: PropTypes.bool,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  seekPending: PropTypes.number,
  isVisible: PropTypes.bool,
};

export default withModals(PlayerControlsWrapper);
